"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { usePathname, useSearchParams } from "next/navigation";
import PiwikProSdk, { PageViews } from "@piwikpro/react-piwik-pro";
import { isMobile } from "react-device-detect";
import { useTrackingStore } from "@/stores";
import { CountryVersionEnum } from "@/services/type";
import { getCountryVersionBasedOnDomain, isStagingDomain } from "@/utils/urls";
import { pushGTMPageDataLayer } from "@/utils/tracking/buildDataLayer";
const PIWIK_CONTAINER_URL = "https://walhalla.piwik.pro";
const VISITOR_COUNTRY_TIMEOUT_MS = 3000;
async function fetchVisitorCountry(signal: AbortSignal): Promise<string | null> {
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH ?? "";
  const response = await fetch(`${basePath}/api/pjpp/career/visitor_country`, {
    cache: "no-store",
    credentials: "same-origin",
    signal
  });
  if (!response.ok) throw new Error("Visitor country lookup failed");
  const result = (await response.json()) as {
    country_code?: unknown;
  };
  const code = result?.country_code;
  return typeof code === "string" && /^[A-Z]{2}$/.test(code) ? code : null;
}
const PIWIK_CONTAINER_ID_BY_COUNTRY: Record<CountryVersionEnum, {
  staging: string;
  production: string;
}> = {
  [CountryVersionEnum.unitedState]: {
    staging: "1200d186-ba7f-4020-ba95-725cb5875a88",
    production: "24b309d7-0588-4caa-97f8-b43cd7044b36"
  },
  [CountryVersionEnum.germany]: {
    staging: "58ac8b82-2dc8-4270-94c1-886f93b1abba",
    production: "734c74fb-2b27-4d2a-ae7b-f8933ee771ff"
  },
  [CountryVersionEnum.austria]: {
    staging: "1704c421-21d2-46c0-acdb-8e9bd00f1b7d",
    production: "d1d2af2e-68e5-4e17-8ef6-d27a8d2f4c92"
  },
  [CountryVersionEnum.belgium]: {
    staging: "2e119e6b-0f3c-4a2a-be1c-9e831417013b",
    production: "cd742421-c693-4621-ba45-a831b73438c3"
  },
  [CountryVersionEnum.switzerlandDe]: {
    staging: "fe4dd03b-e922-4a00-93a4-770ac79286ca",
    production: "dd76c8b5-fd3f-4337-9a4f-b2c8e5b44a94"
  },
  [CountryVersionEnum.switzerlandFr]: {
    staging: "afad9c91-5237-462b-b149-e6d308c26d52",
    production: "21ad280e-d9b8-4bd0-b970-90009d6c2036"
  },
  [CountryVersionEnum.europe]: {
    staging: "b2595d44-6aff-4cb3-8d6e-978bb4d6d64b",
    production: "e5a24846-b690-4d02-a11d-98b454b2e862"
  },
  [CountryVersionEnum.spain]: {
    staging: "7f037735-aaa0-421d-9240-538fc9540202",
    production: "f0af16c0-1097-4eb8-8f85-f1fa058ed1a3"
  },
  [CountryVersionEnum.france]: {
    staging: "1eb587ea-99b4-4839-9ab6-59ecae1948ef",
    production: "c7a9dc32-aae7-468f-8f5b-52c2a14647ed"
  },
  [CountryVersionEnum.italy]: {
    staging: "3df7f01c-541f-4bb1-be7b-dc0ce75f15f7",
    production: "e76c090d-b9d7-4c89-9b3a-8e9c7543d7d1"
  },
  [CountryVersionEnum.unitedKingdom]: {
    staging: "8edc1336-13b7-4db1-a696-a8b6db376fc6",
    production: "9e26bf39-fd47-4ded-b952-d34d9fa88691"
  },
  [CountryVersionEnum.netherlands]: {
    staging: "615ee78c-7673-4a1c-9da1-d111f851dee0",
    production: "2272be08-b412-471d-adec-03b987645efd"
  }
};
export default function PiwikPro() {
  const $ = _c(8);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isInitialized = React.useRef(false);
  const [visitorCountry, setVisitorCountry] = React.useState(undefined);
  const {
    setTrackingConsent
  } = useTrackingStore();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (process.env.NEXT_PUBLIC_API_MOCKING === "enabled") {
        return;
      }
      let cancelled = false;
      const controller = new AbortController();
      const timeout = window.setTimeout(() => {
        cancelled = true;
        controller.abort();
        setVisitorCountry(null);
      }, VISITOR_COUNTRY_TIMEOUT_MS);
      fetchVisitorCountry(controller.signal).then(country => {
        if (!cancelled) {
          setVisitorCountry(country);
        }
      }).catch(() => {
        if (!cancelled) {
          setVisitorCountry(null);
        }
      }).finally(() => {
        window.clearTimeout(timeout);
      });
      return () => {
        cancelled = true;
        window.clearTimeout(timeout);
        controller.abort();
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  React.useEffect(t0, t1);
  let t2;
  let t3;
  if ($[2] !== pathname || $[3] !== searchParams || $[4] !== setTrackingConsent || $[5] !== visitorCountry) {
    t2 = () => {
      if (process.env.NEXT_PUBLIC_API_MOCKING === "enabled") {
        return;
      }
      setTrackingConsent(true);
      if (visitorCountry === undefined) {
        return;
      }
      const countryVersion = getCountryVersionBasedOnDomain(window.location.href);
      const containers = countryVersion ? PIWIK_CONTAINER_ID_BY_COUNTRY[countryVersion] : null;
      if (!containers) {
        return;
      }
      const containerId = isStagingDomain() ? containers.staging : containers.production;
      if (!containerId) {
        return;
      }
      if (!isInitialized.current) {
        pushGTMPageDataLayer({
          pathname: pathname ?? "/",
          searchString: window.location.search,
          url: window.location.href,
          referrer: document.referrer,
          appType: isMobile ? "mobile_web" : "desktop_web",
          countryVersionName: countryVersion,
          affiliateName: searchParams?.get("affiliate") ?? ""
        });
        (window as Window & {
          pjppGeoipCountry?: string | null;
        }).pjppGeoipCountry = visitorCountry;
        PiwikProSdk.initialize(containerId, PIWIK_CONTAINER_URL);
        isInitialized.current = true;
        return;
      }
      PageViews.trackPageView();
    };
    t3 = [pathname, searchParams, setTrackingConsent, visitorCountry];
    $[2] = pathname;
    $[3] = searchParams;
    $[4] = setTrackingConsent;
    $[5] = visitorCountry;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  React.useEffect(t2, t3);
  return null;
}